import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAuthSessionValid, getAuthSession, updateAuthSession } from '../utils/auth';
import { getRoleById } from '../firebase/services/roleService';
import { Role } from '../types/rbac';

type PrivateRouteProps = {
  children: React.ReactNode;
  allowedRoles: string[];
};

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, allowedRoles }) => {
  const { currentUser, userProfile } = useAuth();
  const location = useLocation();

  const [validAuthSession, setValidAuthSession] = useState<boolean | null>(null);
  const [session, setSession] = useState<any>(null);
  const [userRoleName, setUserRoleName] = useState<string | null>(null);
  const [loadingRole, setLoadingRole] = useState<boolean>(true);
  const [authChecking, setAuthChecking] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;
    const checkAuthAndRole = async () => {
      try {
        // Start authentication check
        setAuthChecking(true);
        console.log(`Checking auth for route: ${location.pathname}`);
        
        // Check if auth session is valid - AWAIT the promise
        const valid = await isAuthSessionValid();
        const sess = getAuthSession();
        
        console.log(`Auth check result: ${valid}`, {
          path: location.pathname,
          validAuthSession: valid,
          hasSession: !!sess,
          hasCurrentUser: !!currentUser,
          userProfileExists: !!userProfile,
        });
        
        if (!mounted) return;
        
        // Update state with auth result
        setValidAuthSession(valid);
        setSession(sess);
        setAuthChecking(false);
        
        // Get role ID either from user profile or session
        const roleId = currentUser ? userProfile?.roleId : sess?.roleId;
        
        // If we have a role ID, fetch the role details
        if (roleId) {
          setLoadingRole(true);
          try {
            const roleDoc = await getRoleById(roleId);
            if (!mounted) return;
            setUserRoleName(roleDoc?.name || null);
            
            console.log(`Role check result:`, {
              roleId,
              roleName: roleDoc?.name,
              allowedRoles
            });
          } catch (roleError) {
            console.error('Error fetching role:', roleError);
            if (!mounted) return;
            setUserRoleName(null);
          } finally {
            if (mounted) setLoadingRole(false);
          }
        } else {
          if (mounted) {
            setUserRoleName(null);
            setLoadingRole(false);
          }
        }
      } catch (error) {
        console.error('Error in auth check:', error);
        if (mounted) {
          setValidAuthSession(false);
          setAuthChecking(false);
          setLoadingRole(false);
        }
      }
    };
    
    checkAuthAndRole();
    return () => { mounted = false; };
  }, [currentUser, userProfile, location.pathname, allowedRoles]);

  // Show loading spinner while checking session
  if (validAuthSession === null || loadingRole || authChecking) {
    return <div>Loading...</div>;
  }

  // Never allow access to staff/admin routes if session is invalid
  if ((location.pathname.startsWith('/staff') || location.pathname.startsWith('/admin')) && !validAuthSession) {
    console.log('Blocking access to protected route - invalid session:', location.pathname);
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  // Accept either Firebase auth OR valid auth session
  if (!currentUser && !validAuthSession) {
    console.log('Redirecting to auth - no user or valid session');
    return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  }

  // Check role access using case-insensitive comparison
  if (userRoleName) {
    const hasAllowedRole = allowedRoles.some(allowedRole => 
      userRoleName.toLowerCase() === allowedRole.toLowerCase()
    );

    if (!hasAllowedRole) {
      console.log(`User role ${userRoleName} not allowed for this route. Allowed roles: ${allowedRoles.join(', ')}`);
      
      // Redirect based on user role (case-insensitive)
      const roleLower = userRoleName.toLowerCase();
      if (roleLower === 'customer') {
        return <Navigate to="/customer/dashboard" replace />;
      } else if (roleLower === 'staff') {
        return <Navigate to="/staff/dashboard" replace />;
      } else if (roleLower === 'admin') {
        return <Navigate to="/admin/dashboard" replace />;
      } else {
        return <Navigate to="/" replace />;
      }
    }
  }

  console.log(`Access granted to ${location.pathname} for user with role: ${userRoleName}`);
  return <>{children}</>;
};

export default PrivateRoute; 